import React, { useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { ArrowRight, InfoIcon } from 'lucide-react';

interface StatePropertyTaxPageProps {
  stateName: string;
  processDescription: string;
  noticeOfValueMailed: string;
  noticeOfValueMailedDateExplanation: string;
  valuationAsOfDate: string;
  valuationAsOfDateExplanation: string;
  deadlineExplanation: string;
  stateStatute?: string;
}

const StatePropertyTaxPage: React.FC<StatePropertyTaxPageProps> = ({
  stateName,
  processDescription,
  noticeOfValueMailed,
  noticeOfValueMailedDateExplanation,
  valuationAsOfDate,
  valuationAsOfDateExplanation,
  deadlineExplanation,
  stateStatute
}) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Clean up text formatting
  const cleanText = (text: string) => {
    return text
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Create SEO-friendly description
  const seoDescription = `Learn about ${stateName} property tax appeal procedures, deadlines, and valuation processes. Get help with your ${stateName} property tax assessment appeal.`;

  // Set document title and meta tags
  useEffect(() => {
    document.title = `${stateName} Property Tax Procedures - Appeal Your Assessment | PropertyTaxHero`;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoDescription);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = seoDescription;
      document.head.appendChild(meta);
    }

    // Update meta keywords
    const keywords = `${stateName} property tax, ${stateName} assessor, ${stateName} property tax appeal, ${stateName} property tax protest, ${stateName} tax assessment, property valuation ${stateName}`;
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = keywords;
      document.head.appendChild(meta);
    }
  }, [stateName, seoDescription]);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-tax-backgroundLight to-white">        
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-tax-deepBlue mb-6">
              {stateName} Property Tax Procedures
            </h1>
            <p className="text-xl text-tax-neutralGray max-w-3xl mx-auto leading-relaxed">
              Complete guide to appealing your property tax assessment in {stateName}
            </p>
          </div>

          {/* Important Notice */}
          <Alert className="mb-8 border-tax-accentBlue bg-tax-accentBlue/5">
            <InfoIcon className="h-4 w-4" />
            <AlertDescription className="text-tax-neutralGray">
              Property taxes are generally administered at the county or township level. Please verify with your local assessor the exact procedures and due dates. The following is a general guide for the state, but your local municipality may differ. Enter your property's address on <Link to="/" className="text-tax-accentBlue font-semibold hover:text-tax-brightBlue underline">PropertyTaxHero.com</Link> to receive personalized instructions for your property's local government.
            </AlertDescription>
          </Alert>

          {/* Call to Action */}
          <div className="text-center mb-8">
            <Card className="p-8 bg-tax-backgroundLight border-tax-accentBlue/20">
              <h2 className="text-3xl font-bold mb-4 text-tax-deepBlue">
                Get Personalized Help for Your {stateName} Property
              </h2>
              <p className="text-xl mb-8 text-tax-neutralGray max-w-2xl mx-auto">
                Enter your property address to receive customized appeal guidance and deadline reminders for your specific county
              </p>
              <Link to="/">
                <Button 
                  size="lg"
                  className="bg-tax-deepBlue hover:bg-tax-brightBlue text-white font-bold text-lg px-8 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  Start Your Free Assessment
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </Link>
            </Card>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Appeal Procedures */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Procedures To Appeal Your Taxable Valuation
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed">
                  {cleanText(processDescription)}
                </p>
              </div>
            </Card>

            {/* Notice of Value */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Notice of Value (NOV)
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed mb-4">
                  You should receive a notice of value or similar document from your assessor. This should be around <strong>{noticeOfValueMailed}</strong>.
                </p>
                <p className="leading-relaxed">
                  {cleanText(noticeOfValueMailedDateExplanation)}
                </p>
              </div>
            </Card>

            {/* Property Valuation */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Property Valuation
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed mb-4">
                  Properties are valued as of <strong>{valuationAsOfDate}</strong>.
                </p>
                <p className="leading-relaxed">
                  {cleanText(valuationAsOfDateExplanation)}
                </p>
              </div>
            </Card>

            {/* Deadlines */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Deadlines to Appeal
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed">
                  {cleanText(deadlineExplanation)}
                </p>
              </div>
            </Card>

            {/* State Statute Link */}
            {stateStatute && (
              <Card className="p-6">
                <h2 className="text-xl font-bold text-tax-deepBlue mb-4">
                  {stateName} Property Tax Laws
                </h2>
                <p className="text-tax-neutralGray mb-4">
                  For complete legal details and current statutes governing property tax in {stateName}:
                </p>
                <a 
                  href={stateStatute} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button className="bg-tax-deepBlue hover:bg-tax-brightBlue text-white transition-colors">
                    View {stateName} Property Tax Statutes
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </Card>
            )}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <Card className="p-8 bg-tax-backgroundLight">
              <h3 className="text-2xl font-bold text-tax-deepBlue mb-4">
                Ready to Challenge Your {stateName} Property Tax Assessment?
              </h3>
              <p className="text-lg text-tax-neutralGray mb-6">
                Visit PropertyTaxHero.com and enter your property address for personalized information and free help preparing a valuation appeal.
              </p>
              <Link to="/">
                <Button 
                  size="lg" 
                  className="bg-tax-deepBlue hover:bg-tax-brightBlue text-white transition-colors"
                >
                  Get Free Help Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StatePropertyTaxPage;